import React, { useCallback } from 'react';

import { useGlobalSearchParams, useRouter } from 'expo-router';
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';

import SanityBlockRenderer from 'components/sanity-block-renderer';
import { useLoyaltyUserBestPromotionQuery } from 'generated/graphql-gateway';
import {
  ISystemwidePromotionMessageQuery,
  useSystemwidePromotionMessageQuery,
} from 'generated/sanity-graphql';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import { ActivatablePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/activatable-promo-ribbon';
import { SimplePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/simple-promo-ribbon';
import { ModalAuthTrigger } from 'state/auth/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useUIContext } from 'state/ui';
import { ZIndex, primitive } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';

type SanityPromotion = ISystemwidePromotionMessageQuery['SystemwidePromotion'];

let alreadyLoadedOnce = false;

export const PromoMessageRibbon = () => {
  const { activatePromo } = useGlobalSearchParams<{ activatePromo?: string }>();
  const router = useRouter();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;
  const autoActivate = activatePromo === 'true';

  useAuthRequiredInModal({
    skip: !loyaltyEnabled || !autoActivate,
    trigger: ModalAuthTrigger.Global,
    // Prevent the modal from being displayed again on page refresh after user closes it
    onModalClose: () => router.setParams({ activatePromo: '' }),
  });

  const { promoMessageHeightRef } = useUIContext();

  const {
    data: bestPromotionData,
    refetch: refetchBestPromotion,
  } = useLoyaltyUserBestPromotionQuery({
    variables: { loyaltyId },
    skip: !loyaltyEnabled || !loyaltyId,
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      const promotion = data?.loyaltyUserV2?.bestPromotion;

      // Clear the activatePromo param if the promotion is already activated or
      // doesn't require activation
      if (activatePromo && promotion && (!promotion?.mustActivate || promotion?.userActivatedAt)) {
        router.setParams({ activatePromo: '' });
      }
    },
  });

  const bestPromotion = bestPromotionData?.loyaltyUserV2?.bestPromotion;
  const { id: promotionId, sanityId, mustActivate, userActivatedAt } = bestPromotion ?? {};

  // alreadyLoadedOnce is a hack to prevent skipping the query which makes it null and flashing occurs
  const skip = !sanityId || (!alreadyLoadedOnce && (!loyaltyEnabled || loyaltyLoading));

  const { data } = useSystemwidePromotionMessageQuery({
    variables: { id: sanityId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const sanityPromotion = data?.SystemwidePromotion;

  const handleActivated = useCallback(async () => {
    await refetchBestPromotion();
    if (autoActivate) {
      router.setParams({ activatePromo: '' });
    }
  }, [refetchBestPromotion, autoActivate, router]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      promoMessageHeightRef.current = event.nativeEvent.layout.height;
    },
    [promoMessageHeightRef]
  );

  if (data) {
    alreadyLoadedOnce = true;
  }

  const message = mustActivate
    ? renderRibbonMessage({ sanityPromotion, userActivatedAt, mustActivate })
    : renderRibbonMessage({ sanityPromotion });

  if (!message) {
    promoMessageHeightRef.current = 0;
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      {mustActivate ? (
        <ActivatablePromoRibbon
          sanityPromotion={sanityPromotion}
          message={message}
          loyaltyId={loyaltyId!}
          promotionId={promotionId!}
          userActivatedAt={userActivatedAt}
          onActivated={handleActivated}
          autoActivate={autoActivate}
        />
      ) : (
        <SimplePromoRibbon message={message} />
      )}
    </View>
  );
};

function renderRibbonMessage({
  userActivatedAt,
  sanityPromotion,
  mustActivate,
}: {
  userActivatedAt?: string | null;
  sanityPromotion?: SanityPromotion;
  mustActivate?: boolean | null;
}): React.ReactElement | null {
  const promoMessage = sanityPromotion?.promoMessage?.locale;
  const blockContent = userActivatedAt
    ? sanityPromotion?.activatedPromoMessageBlock?.locale
    : sanityPromotion?.promoMessageBlock?.locale;

  if (blockContent) {
    return (
      <SanityBlockRenderer
        content={blockContent}
        style={styles.blockRendererWrapper}
        textProps={{
          color: primitive.$bbqBrown,
          textAlign: mustActivate ? 'left' : 'center',
          fontSize: fontSizes.xs,
        }}
      />
    );
  }

  if (promoMessage) {
    return (
      <Text style={[styles.ribbonText, mustActivate && styles.activablePromotionText]}>
        {promoMessage}
      </Text>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  blockRendererWrapper: {
    zIndex: ZIndex.MAX,
  },
  ribbonText: {
    fontFamily: 'Flame-Regular',
    fontSize: fontSizes.xs,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    zIndex: ZIndex.MAX,
  },
  activablePromotionText: {
    textAlign: 'left',
  },
});
